import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { Question, QuestionResponse, Section } from '../../../../interfaces/questions.interface';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { AuthService } from '../../../../../auth/services/auth.service';
import { OrganizationsService } from '../../../../../organizations/services/organizations.service';
import { ComponentAbleToDeactivate } from '../../../../../../../common-modules/guards/can-deactivate-guard.service';
import { LeavePageService } from '../../../../../../../common-modules/services/leave-page.service';
import _ from 'lodash';

export interface EmptyState {
    show: boolean;
    isActionClicked: boolean;
}

@Component({
    selector: 'app-questionnaire',
    templateUrl: './questionnaire.component.html',
    styleUrls: ['./questionnaire.component.scss'],
    standalone: false,
})
export class QuestionnaireComponent implements OnDestroy, ComponentAbleToDeactivate, OnInit {
    public isLoading = false;
    public routeSubscription: Subscription;
    public loadQuestionnaireSubscription: Subscription;
    public isQuestionnaireLoadedSubscription: Subscription;
    public disableSubmitForReview = true;
    public allQuestionnaireFilteredLoaded = false;
    public scrollToQuestion: QuestionResponse;

    constructor(
        public router: Router,
        public route: ActivatedRoute,
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cdr: ChangeDetectorRef,
        public authService: AuthService,
        public organizationService: OrganizationsService,
        public leavePageService: LeavePageService,
    ) {
        this.customerQuestionnaireService.questionnaireResumed = !!localStorage.getItem('isQuestionnaireResumed');

        this.routeSubscription = this.route.params.subscribe((params) => {
            if (!params.id) {
                this.commonQuestionnaireService.reviewMode = false;
                return;
            }

            this.commonQuestionnaireService.setCompanyId(params.id);

            this.commonQuestionnaireService.reviewMode = true;
            this.commonQuestionnaireService.createMode = false;

            if (this.commonQuestionnaireService.questionnaireId) {
                this.customerQuestionnaireService.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, params.id);
            } else {
                this.loadQuestionnaireSubscription = this.commonQuestionnaireService
                    .questionnnaireIdLoadedObs()
                    .subscribe((questionnaireIdLoaded) => {
                        this.customerQuestionnaireService.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId, params.id);
                    });
            }
        });

        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            this.commonQuestionnaireService.questionsFilteredObs().subscribe((loaded) => {
                this.allQuestionnaireFilteredLoaded = loaded;
            });
        }

        if (this.commonQuestionnaireService.createMode) {
            this.isQuestionnaireLoadedSubscription = this.cstQuestionnaireService.isQuestionnaireLoaded().subscribe((loaded) => {
                if (loaded) {
                    this.isLoading = false;
                }
                this.allQuestionnaireFilteredLoaded = loaded;
            });
        }

        if (this.commonQuestionnaireService.reviewMode) {
            this.isQuestionnaireLoadedSubscription = this.customerQuestionnaireService.isQuestionnaireLoaded().subscribe((loaded) => {
                if (loaded) {
                    this.isLoading = false;
                }
                this.allQuestionnaireFilteredLoaded = loaded;
            });
        }
    }

    ngOnInit() {
        if (this.commonQuestionnaireService.createMode) {
            this.cstQuestionnaireService.currentQuestionForm = null;
            this.cstQuestionnaireService.resetQuestionnaireDisplay();
            this.cstQuestionnaireService.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId);
        }
    }

    ngOnDestroy() {
        this.routeSubscription?.unsubscribe();
        this.loadQuestionnaireSubscription?.unsubscribe();
        this.isQuestionnaireLoadedSubscription?.unsubscribe();
    }

    public onAllQuestionsReviewed($event: any) {
        this.disableSubmitForReview = false;
    }

    public showStart() {
        return (
            this.customerQuestionnaireService.calculateAnsweredQuestions() === 0 &&
            !this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode &&
            !this.customerQuestionnaireService.questionnaireResumed
        );
    }

    public showCheckSubmit() {
        return (
            !this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode &&
            this.customerQuestionnaireService.showCheckSubmit
        );
    }

    public showSlideshow() {
        const hasSubmitted = Boolean(localStorage.getItem('hasSubmittedQuestionnaire')) || false;

        return (
            (this.commonQuestionnaireService.createMode ||
                this.commonQuestionnaireService.reviewMode ||
                this.customerQuestionnaireService.questionnaireResumed ||
                (this.customerQuestionnaireService.areAllQuestionsAnswered() && !hasSubmitted)) &&
            !this.customerQuestionnaireService.showCheckSubmit
        );
    }

    public showResume() {
        const hasSubmitted = Boolean(localStorage.getItem('hasSubmittedQuestionnaire')) || false;
        return (
            (this.customerQuestionnaireService.answeredAtLeastOneNonRequiredQuestion() ||
                (this.customerQuestionnaireService.calculateAnsweredQuestions() > 0 && !this.showFinishAllMandatory())) &&
            !this.customerQuestionnaireService.areAllQuestionsAnswered() &&
            !this.customerQuestionnaireService.questionnaireResumed &&
            !this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode &&
            !this.customerQuestionnaireService.showCheckSubmit &&
            this.customerQuestionnaireService.displayVersion === 'full' &&
            !hasSubmitted
        );
    }

    public showNoQuestions() {
        return (
            this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode &&
            this.cstQuestionnaireService.processedEntities.length === 0
        );
    }

    public showFinishAllQuestionnaire() {
        const hasSubmitted = Boolean(localStorage.getItem('hasSubmittedQuestionnaire')) || false;

        return (
            this.customerQuestionnaireService.areAllQuestionsAnswered() &&
            !this.customerQuestionnaireService.questionnaireResumed &&
            !this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode &&
            hasSubmitted
        );
    }

    public showFinishAllMandatory() {
        return (
            JSON.parse(localStorage.getItem('hasSubmittedQuestionnaire') || 'false') &&
            this.customerQuestionnaireService.areAllMandatoryQuestionsAnswered() &&
            !this.customerQuestionnaireService.areAllQuestionsAnswered() &&
            !this.customerQuestionnaireService.questionnaireResumed &&
            !this.commonQuestionnaireService.createMode &&
            !this.commonQuestionnaireService.reviewMode
        );
    }

    public scrollToQuestionReceived(question: QuestionResponse) {
        this.scrollToQuestion = question;
    }

    /* Function used each time the Resume button is clicked */
    public onResumeQuestionnaire(status: boolean, event: 'start' | 'resume', isContinue?: boolean) {
        this.customerQuestionnaireService.questionnaireResumed = status;
        localStorage.setItem('isQuestionnaireResumed', 'true');
        this.customerQuestionnaireService.questionnaireResumed = status;

        let allQuestions =
            this.customerQuestionnaireService.displayVersion === 'full'
                ? this.customerQuestionnaireService.processedFullSections
                : this.customerQuestionnaireService.processedCondensedSections;

        setTimeout(() => {
            if (event === 'start') {
                /*If start button was clicked then we need to go to the first question*/
                this.customerQuestionnaireService.setQuestion(allQuestions[0]);
                this.customerQuestionnaireService.sections[0].isExpanded = true;
            } else {
                if (isContinue && this.customerQuestionnaireService.areAllMandatoryQuestionsAnswered()) {
                    this.customerQuestionnaireService.setDisplayVersion('full');
                    allQuestions = this.customerQuestionnaireService.processedFullSections;
                } else {
                    if (this.customerQuestionnaireService.displayVersion === 'full') {
                        allQuestions = this.customerQuestionnaireService.processedFullSections;
                    } else {
                        allQuestions = this.customerQuestionnaireService.processedFullSections.filter((x) => x.required);
                    }
                }
                /** If resume button was clicked we need to go either to first unanswered question
                 * OR
                 * If it has a review left by CIFC which is unresolved to that question */
                let lastUnansweredQuestion = allQuestions.find((x: Question) => {
                    return (
                        ((x as unknown as QuestionResponse).cifc_review &&
                            !(x as unknown as QuestionResponse).cifc_review.solved &&
                            !(x as unknown as QuestionResponse).cifc_review.answered) ||
                        !(x as unknown as QuestionResponse).body.answered
                    );
                }) as unknown as QuestionResponse;

                if (!lastUnansweredQuestion) {
                    lastUnansweredQuestion = allQuestions[0] as unknown as QuestionResponse;
                }

                /** Expand the section in which the determined question is*/
                if (lastUnansweredQuestion) {
                    this.commonQuestionnaireService.onQuestionSelected(lastUnansweredQuestion);
                    const findQuestion = allQuestions.find((x: Question) => x.id === lastUnansweredQuestion.id);

                    if (findQuestion) {
                        this.customerQuestionnaireService.setQuestion(findQuestion);
                        const findSection = this.customerQuestionnaireService.sections.find(
                            (x: Section) => x.id === findQuestion.section_id,
                        );
                        if (findSection) {
                            findSection.isExpanded = true;
                        }
                        this.cdr.detectChanges();
                    }
                }
            }
        }, 100);
    }

    public publishQuestionnaire() {
        this.commonQuestionnaireService.isPublishInProgress = true;
        this.isLoading = true;
        this.cstQuestionnaireService.buildQuestionnaireObject(this.commonQuestionnaireService.questionnaireId);
        this.isLoading = false;
    }

    public async submitReviewActionButton() {
        if (this.customerQuestionnaireService.getModifiedQuestionsLength() > 0) {
            await this.customerQuestionnaireService.submitQuestionsForReview();
        }
        this.commonQuestionnaireService.startReviewIfNotStarted(this.commonQuestionnaireService.reviewCompanyId).subscribe(async (val) => {
            await this.router.navigate(['/cq/browse/']);
        });
    }

    public canDeactivate() {
        if (!this.commonQuestionnaireService.createMode || !this.cstQuestionnaireService.currentQuestionForm) {
            return true;
        }
        if (this.cstQuestionnaireService.currentQuestionForm?.group.valid) {
            // exit page without displaying any warnings, if submitting or no fields have been filled
            return true;
        }
        const leavePageConfig = {
            title: $localize`You have unfinished questions`,
            description: $localize`All unfinished questions will be deleted if you decide to leave this page`,
            primaryButton: {
                text: $localize`Leave this page`,
                action: () => {
                    if (!this.cstQuestionnaireService.currentQuestionForm?.group.valid) {
                        this.cstQuestionnaireService.currentSection.questions.forEach((question) => {
                            if (
                                this.cstQuestionnaireService.validateQuestion(question) &&
                                !this.cstQuestionnaireService.isQuestionInInitialQuestions(question.id)
                            ) {
                                this.cstQuestionnaireService.deleteQuestionFromFrontend(
                                    this.cstQuestionnaireService.currentSection.id,
                                    question,
                                );
                            } else if (
                                this.cstQuestionnaireService.validateQuestion(question) &&
                                this.cstQuestionnaireService.isQuestionInInitialQuestions(question.id)
                            ) {
                                this.cstQuestionnaireService.sections.forEach((s) => {
                                    const index = s.questions.findIndex((x) => x.id === question.id);
                                    if (index > -1) {
                                        (s.questions[index] as any) = _.cloneDeep(
                                            this.cstQuestionnaireService.initialQuestions.find((x) => x.id === question.id),
                                        );
                                    }
                                });
                                this.cstQuestionnaireService.sectionsSubject.next(this.cstQuestionnaireService.sections);
                            }
                        });
                        this.leavePageService.resetLeavePageConfig();
                    }
                },
            },
            cancelButton: { text: $localize`Stay on this page` },
        };
        this.leavePageService.changeLeavePageConfig(leavePageConfig);
        return this.leavePageService.navigateThroughLeavePageDialog();
    }
}
